/**
 * Unified API Service Layer
 * Abstracts GraphQL vs REST API differences and provides a consistent interface
 */

import { apolloClient } from '../graphql/client';
import ApiService from './api.js';
import DetectionAPI from './detectionApi';
import { 
  GET_DASHBOARD_DATA, 
  GET_OFFERS, 
  GET_PRODUCTS, 
  GET_MERCHANTS, 
  GET_CITIES,
  GET_DASHBOARD_STATS,
  GET_CATEGORY_PERFORMANCE,
  GET_MERCHANT_PERFORMANCE
} from '../graphql/queries';

export interface DashboardData {
  stats: {
    totalOffers: number;
    activeOffers: number;
    totalProducts: number;
    totalMerchants: number;
    avgConfidence: number;
  };
  recentOffers: any[];
  recentProducts: any[];
  merchants: any[];
  cities: any[];
}

export interface ApiFilters {
  search?: string;
  merchantId?: number;
  cityId?: number;
  category?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

class UnifiedApiService {
  private useGraphQL: boolean;

  constructor(useGraphQL = true) {
    this.useGraphQL = useGraphQL;
  }

  /**
   * Toggle between GraphQL and REST APIs
   */
  setUseGraphQL(useGraphQL: boolean) {
    this.useGraphQL = useGraphQL;
  }

  /**
   * Get dashboard data - optimized for GraphQL with single request
   */
  async getDashboardData(): Promise<DashboardData> {
    if (this.useGraphQL) {
      try {
        console.log('🔍 Executing GraphQL dashboard query...');
        const result = await apolloClient.query({
          query: GET_DASHBOARD_DATA,
          fetchPolicy: 'cache-first'
        });
        
        console.log('📊 GraphQL response:', result);
        const { data } = result;

        // Check if data exists before accessing nested properties
        if (!data || !data.dashboardStats) {
          console.error('GraphQL returned empty data:', data);
          console.error('Full GraphQL result:', result);
          throw new Error('Empty GraphQL response');
        }

        // Transform GraphQL response to expected format
        return {
          stats: {
            totalOffers: data.dashboardStats?.offers?.totalOffers || 0,
            activeOffers: data.dashboardStats?.offers?.activeOffers || 0,
            totalProducts: data.dashboardStats?.products?.totalProducts || 0,
            totalMerchants: data.dashboardStats?.merchants?.totalMerchants || 0,
            avgConfidence: (data.dashboardStats?.products?.avgConfidence || 0) * 100, // Convert to percentage
          },
          recentOffers: this.transformOffersFromGraphQL(data.recentOffers || []),
          recentProducts: this.transformProductsFromGraphQL(data.recentProducts || []),
          merchants: this.transformMerchantsFromGraphQL(data.merchants || []),
          cities: this.transformCitiesFromGraphQL(data.cities || []),
        };
      } catch (error) {
        console.warn('GraphQL dashboard query failed, falling back to REST:', error);
        return this.getDashboardDataRest();
      }
    } else {
      return this.getDashboardDataRest();
    }
  }

  /**
   * Get offers with filtering and pagination
   */
  async getOffers(filters: ApiFilters = {}) {
    if (this.useGraphQL) {
      try {
        const { data } = await apolloClient.query({
          query: GET_OFFERS,
          variables: {
            filters: {
              merchant_id: filters.merchantId,
              city_id: filters.cityId,
              search: filters.search,
              status: filters.status?.toUpperCase(),
            },
            pagination: {
              limit: filters.limit || 20,
              offset: filters.offset || 0,
            },
            sort: {
              field: 'created_at',
              direction: 'DESC',
            },
          },
          fetchPolicy: 'cache-first'
        });

        return this.transformOffersFromGraphQL(data.offers);
      } catch (error) {
        console.warn('GraphQL offers query failed, falling back to REST:', error);
        return ApiService.getAllOffers(filters);
      }
    } else {
      return ApiService.getAllOffers(filters);
    }
  }

  /**
   * Get products with filtering and pagination
   */
  async getProducts(filters: ApiFilters = {}) {
    if (this.useGraphQL) {
      try {
        const { data } = await apolloClient.query({
          query: GET_PRODUCTS,
          variables: {
            filters: {
              offer_id: filters.merchantId, // Assuming merchantId maps to offerId in this context
              category: filters.category,
              search: filters.search,
            },
            pagination: {
              limit: filters.limit || 50,
              offset: filters.offset || 0,
            },
            sort: {
              field: 'created_at',
              direction: 'DESC',
            },
          },
          fetchPolicy: 'cache-first'
        });

        return this.transformProductsFromGraphQL(data.products);
      } catch (error) {
        console.warn('GraphQL products query failed, falling back to REST:', error);
        return ApiService.getProducts(filters);
      }
    } else {
      return ApiService.getProducts(filters);
    }
  }

  /**
   * Get merchants
   */
  async getMerchants(filters: ApiFilters = {}) {
    if (this.useGraphQL) {
      try {
        const { data } = await apolloClient.query({
          query: GET_MERCHANTS,
          variables: {
            active_only: true,
          },
          fetchPolicy: 'cache-first'
        });

        return this.transformMerchantsFromGraphQL(data.merchants);
      } catch (error) {
        console.warn('GraphQL merchants query failed, falling back to REST:', error);
        return ApiService.getMerchants(filters);
      }
    } else {
      return ApiService.getMerchants(filters);
    }
  }

  /**
   * Get cities
   */
  async getCities() {
    if (this.useGraphQL) {
      try {
        const { data } = await apolloClient.query({
          query: GET_CITIES,
          variables: {
            active_only: true,
          },
          fetchPolicy: 'cache-first'
        });

        return this.transformCitiesFromGraphQL(data.cities);
      } catch (error) {
        console.warn('GraphQL cities query failed, falling back to REST:', error);
        return ApiService.getCities();
      }
    } else {
      return ApiService.getCities();
    }
  }

  /**
   * Get analytics data
   */
  async getAnalyticsData() {
    if (this.useGraphQL) {
      try {
        const [statsResult, categoryResult, merchantResult] = await Promise.all([
          apolloClient.query({ query: GET_DASHBOARD_STATS }),
          apolloClient.query({ query: GET_CATEGORY_PERFORMANCE }),
          apolloClient.query({ query: GET_MERCHANT_PERFORMANCE }),
        ]);

        return {
          stats: statsResult.data.dashboardStats,
          categories: categoryResult.data.categoryPerformance,
          merchants: merchantResult.data.merchantPerformance,
        };
      } catch (error) {
        console.warn('GraphQL analytics query failed, falling back to REST:', error);
        // For now, return empty data - can implement REST analytics later
        return {
          stats: null,
          categories: [],
          merchants: [],
        };
      }
    } else {
      // REST analytics implementation would go here
      return {
        stats: null,
        categories: [],
        merchants: [],
      };
    }
  }

  // File operations and actions remain REST-only
  async uploadFiles(files: File[], offerId?: string) {
    if (offerId) {
      return ApiService.uploadOfferFiles(parseInt(offerId), files);
    } else {
      return DetectionAPI.detectProducts(files);
    }
  }

  async processOffer(offerId: string, files: File[]) {
    return ApiService.processOffer(offerId, files);
  }

  async createOffer(data: any) {
    return ApiService.createOffer(data);
  }

  async updateOffer(offerId: string, data: any) {
    return ApiService.updateOffer(offerId, data);
  }

  async deleteOffer(offerId: string) {
    return ApiService.deleteOffer(offerId);
  }

  async exportProducts(filters: any = {}) {
    return DetectionAPI.exportProducts(filters);
  }

  async checkHealth() {
    return ApiService.checkHealth();
  }

  // Helper methods to transform GraphQL responses to REST-compatible format
  private async getDashboardDataRest(): Promise<DashboardData> {
    // Fallback to multiple REST API calls
    const [offersData, productsData, merchantsData, citiesData] = await Promise.all([
      ApiService.getAllOffers({ limit: 5 }).catch(() => []),
      ApiService.getProducts({ limit: 10 }).catch(() => []),
      ApiService.getMerchants().catch(() => []),
      ApiService.getCities().catch(() => []),
    ]);

    // Calculate stats from the data
    const stats = {
      totalOffers: Array.isArray(offersData) ? offersData.length : 0,
      activeOffers: Array.isArray(offersData) ? offersData.filter((o: any) => o.status === 'active').length : 0,
      totalProducts: Array.isArray(productsData) ? productsData.length : 0,
      totalMerchants: Array.isArray(merchantsData) ? merchantsData.length : 0,
      avgConfidence: Array.isArray(productsData) && productsData.length > 0 
        ? productsData.reduce((sum: number, p: any) => sum + (p.confidence_score || p.confidence || 0), 0) / productsData.length * 100
        : 94,
    };

    return {
      stats,
      recentOffers: Array.isArray(offersData) ? offersData : [],
      recentProducts: Array.isArray(productsData) ? productsData : [],
      merchants: Array.isArray(merchantsData) ? merchantsData : [],
      cities: Array.isArray(citiesData) ? citiesData : [],
    };
  }

  private transformOffersFromGraphQL(offers: any[]): any[] {
    return offers.map((offer) => ({
      id: offer.id,
      title: offer.title,
      description: offer.description,
      status: offer.status ? offer.status.toLowerCase() : 'draft',
      merchant_id: offer.merchant?.id,
      merchant_name: offer.merchant?.nameArabic || offer.merchantName,
      products_count: offer.productsCount || 0,
      views_count: offer.viewsCount || 0,
      start_date: offer.startDate,
      end_date: offer.validUntil,
      created_at: offer.createdAt,
      updated_at: offer.updatedAt,
      cities: offer.cities || [],
      products: offer.products ? this.transformProductsFromGraphQL(offer.products) : undefined,
    }));
  }

  private transformProductsFromGraphQL(products: any[]): any[] {
    return products.map((product) => ({
      id: product.id,
      name_arabic: product.nameArabic,
      name_english: product.nameEnglish,
      brand: product.brand,
      category: product.category,
      old_price: product.extractedData?.priceOld,
      new_price: product.extractedData?.priceNew,
      quantity: product.extractedData?.quantity,
      confidence: product.confidence,
      confidence_score: product.confidence,
      image_url: product.imageUrl,
      original_image_filename: product.originalImageUrl,
      created_at: product.createdAt,
      updated_at: product.updatedAt,
      extracted_data: {
        product_name: product.extractedData?.productName,
        brand: product.extractedData?.brand,
        price_old: product.extractedData?.priceOld,
        price_new: product.extractedData?.priceNew,
        discount: product.extractedData?.discount,
        quantity: product.extractedData?.quantity,
        category: product.extractedData?.category,
      },
    }));
  }

  private transformMerchantsFromGraphQL(merchants: any[]): any[] {
    return merchants.map((merchant) => ({
      id: merchant.id,
      name_arabic: merchant.nameArabic,
      name_english: merchant.nameEnglish,
      name: merchant.nameArabic,
      description: merchant.description,
      logo_url: merchant.logoUrl,
      website: merchant.website,
      phone: merchant.phone,
      is_active: merchant.isActive,
      created_at: merchant.createdAt,
      updated_at: merchant.updatedAt,
      offers_count: merchant.offersCount,
      total_products: merchant.totalProducts,
    }));
  }

  private transformCitiesFromGraphQL(cities: any[]): any[] {
    return cities.map((city) => ({
      id: city.id,
      name_arabic: city.nameArabic,
      name_english: city.nameEnglish,
      region: city.region,
      country: city.country,
      is_active: city.isActive,
    }));
  }
}

// Export singleton instance
export const unifiedApi = new UnifiedApiService();
export default unifiedApi;